<template>
  <section class="device-network-container">
    <div class="header">
      <div class="header-titles">
        <h4>{{$t('network_information')}}</h4>
        <h4 class="device-count">{{devicesToShow.length}} / {{networkDevices.length}} {{$t('devices')}}</h4>
      </div>
      <transition name="fade">
        <div class="custom-button-container">
          <TableSearch :tableTitles="tableTitles" @input="handleInput" />
        </div>
      </transition>
    </div>
    <Table
      v-if="devicesToShow.length"
      :key="componentKey"
      :tableData="devicesToShow"
      :isHidingRowIndex="isMobile"
      class="network-info-table"
      headerFontSize="13px"
      bodyFontSize="15px"
      idField="id"
      :isNested="false"
      :customRowGridStr="gridStyle"
      @cellClicked="cellClick"
    />
    <div v-else-if="!isLoading" class="no-data">
      {{ $t('no_network_data_available') }}
    </div>
    <Loading v-if="isLoading" class="loader" />
  </section>
</template>

<script>
import Table from "@/modules/common/components/customTable/Table"
import util from '../../common/services/utilService';
import { mapGetters } from "vuex"
import TableSearch from '@/modules/common/components/customTable/TableSearch'
import Loading from '@/modules/common/components/Loading'

export default {
  name: 'DeviceNetworkInfo',
  data() {
    return {
      searchBy: '',
      searchTxt: '',
      componentKey: 0,
      tableTitles: [],
      isLoading: false,
      isMobile: false,
    }
  },
  created() {
    this.setTableTitles()
    this.isMobile = window.innerWidth <= 820
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  computed: {
    ...mapGetters(['devicesStatus']),
    networkDevices() {
      if (!this.devicesStatus || !this.devicesStatus.length) return []
      
      const networkInfos = []
      for (const device of this.devicesStatus) {
        if (device.device_type && device.device_type.toLowerCase().includes('pad') && 
            device.room_name && device.version && device.version.tooltip && device.version.tooltip.texts) {
          const networkInfo = this.parseNetworkInfo(device.version.tooltip.texts)
          networkInfos.push({
            device_id: device.device_id,
            room_name: device.room_name,
            device_type: this.$t(device.device_type.toLowerCase()),
            status: device.status,
            tooltipTexts: device.version.tooltip.texts,
            ...networkInfo
          })
        }
      }
      return networkInfos
    },
    devicesToShow() {
      const { networkDevices, searchTxt, searchBy } = this
      let devicesToShow = JSON.parse(JSON.stringify(networkDevices))
      if (!devicesToShow) return []
      
      devicesToShow.forEach(device => {
        delete device.tooltipTexts
      })
      
      return util.filterArrayOfObjectsWithString(devicesToShow, searchTxt, searchBy)
    },
    gridStyle() {
      if (this.isMobile) {
        return '1fr'
      }
      return '0.8fr 1fr 0.8fr 80px 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr 1fr'
    },
  },
  watch: {
    networkDevices: {
      handler() {
        this.setTableTitles()
        this.forceRerender()
      },
      deep: true
    },
    devicesToShow: {
      handler() {
        this.$nextTick(() => this.addDataLabelsToTableCells())
      },
    },
    isMobile() {
      this.$nextTick(() => this.addDataLabelsToTableCells())
    },
  },
  mounted() {
    this.$nextTick(() => this.addDataLabelsToTableCells())
  },
  updated() {
    this.$nextTick(() => this.addDataLabelsToTableCells())
  },
  methods: {
    handleResize() {
      const wasMobile = this.isMobile
      this.isMobile = window.innerWidth <= 820
      if (wasMobile !== this.isMobile) {
        this.forceRerender()
      }
    },
    addDataLabelsToTableCells() {
      if (!this.isMobile || !this.devicesToShow.length) return

      this.$nextTick(() => {
        const firstDevice = this.devicesToShow[0]
        const columnKeys = Object.keys(firstDevice).filter(key => key !== 'id')

        const getLabelForKey = (key) => {
          const translated = this.$t(key)
          return translated !== key ? translated : key.split('_').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ')
        }

        const tableRows = document.querySelectorAll('.network-info-table .table-row:not(.table-head .table-row)')
        tableRows.forEach((row, rowIndex) => {
          const cells = row.querySelectorAll('.table-cell:not(.cell-icon):not(.checkbox-container):not(.triangle-container)')
          cells.forEach((cell, index) => {
            const key = columnKeys[index]
            if (key) {
              cell.setAttribute('data-label', getLabelForKey(key))
            }
          })
        })
      })
    },
    forceRerender() {
      this.componentKey += 1
    },
    cellClick(payload) {
      if (!payload) return
    },
    setTableTitles() {
      if (this.networkDevices.length > 0) {
        const allKeys = new Set(['device_id', 'room_name', 'device_type', 'status'])
        this.networkDevices.forEach(device => {
          if (device.tooltipTexts) {
            device.tooltipTexts.forEach(text => {
              if (text.includes(':')) {
                const key = text.split(':')[0].trim().toLowerCase().replace(/ /g, '_')
                allKeys.add(key)
              }
            })
          }
        })
        allKeys.delete('tooltiptexts')
        this.tableTitles = Array.from(allKeys)
      }
    },
    handleInput(value, searchBy) {
      if (searchBy) this.searchBy = searchBy
      this.searchTxt = value
    },
    parseNetworkInfo(tooltipTexts) {
      const networkInfo = {}
      if (!tooltipTexts || !Array.isArray(tooltipTexts)) return networkInfo
      
      tooltipTexts.forEach(text => {
        if (text.includes(':')) {
          const [key, value] = text.split(':').map(s => s.trim())
          const formattedKey = key.toLowerCase().replace(/ /g, '_')
          networkInfo[formattedKey] = value
        }
      })
      
      return networkInfo
    }
  },
  components: { 
    Table, 
    TableSearch, 
    Loading 
  }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;
@use '../styles/devices-responsive.scss' as devices;

.device-network-container {
  width: 100%;
  max-width: 100%;
  min-height: 54vh;
  background-color: $white;
  box-shadow: 0 0 1px #bdbdbd;
  overflow-x: hidden;
  box-sizing: border-box;

  .header {
    padding: 20px 25px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid $grey-border-color;

    @media (max-width: 820px) {
      flex-direction: column;
      align-items: stretch;
      padding: 15px;
      gap: 12px;
    }

    .header-titles {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 12px 24px;

      h4 {
        font-size: 16px;
        margin: 0;

        &:first-child {
          font-weight: 700;
        }
      }

      .device-count {
        font-weight: $font-weight-regular;
        color: $dark-grey;
        font-size: 14px;
      }
    }
  }

  .network-info-table {
    margin: 0 auto;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    @include devices.devices-panel-table-mobile;
  }

  .custom-button-container {
    flex: 1 1 240px;
    min-width: 200px;
    max-width: 320px;

    @media (max-width: 820px) {
      width: 100%;
      max-width: 100%;
      flex: 1 1 100%;
    }
  }

  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    padding: 24px;
    color: #666;
    font-size: 16px;
    text-align: center;
  }

  .loader {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
